import { Dispatch, FC, SetStateAction } from 'react'
import { Field, FieldProps } from 'formik'
import PerInputNumber from '../custom_components/PerInputNumber'
import PerText from '../custom_components/Text'
import { TFunction } from 'i18next'
import { isMonthlySalaryInRange, MULTI_VALUE_SEPARATOR } from '../../modules/appraisal/contract/helpers'
import { Currency, GovernmentJobTitle } from '../../types'
import { InputNumberProps } from './InputNumber'
import { useCurrency } from '../../context/currency-context'
import { useAmountAndCurrency } from '../../shared/useAmountAndCurrency'

export type BrazilianProps = {
  currentContractSalary?: number
  selectedSalaryWarning?: string
  setSelectedSalaryWarning?: Dispatch<SetStateAction<undefined>>
  setCurrentSalaryWarning?: Dispatch<SetStateAction<undefined>>
  monthlySalaryMinimum?: number
  monthlySalaryMaximum?: number
  monthlySalaryMonthsInYear?: number
  t?: TFunction
  currentGovernmentJobTitle?: GovernmentJobTitle
}

export type BrazilianInputNumberProps = InputNumberProps & BrazilianProps

export const BrazilianFormikInputNumber: FC<BrazilianInputNumberProps> = ({
  name,
  validate,
  minInitialOffset,
  subLabel,
  currentContractSalary,
  selectedSalaryWarning,
  setSelectedSalaryWarning,
  setCurrentSalaryWarning,
  isContractChange,
  monthlySalaryMinimum,
  monthlySalaryMaximum,
  monthlySalaryMonthsInYear,
  t,
  currentGovernmentJobTitle,
  ...restProps
}) => {
  let monthlySalaryMaximumInSalaryCurrency = monthlySalaryMaximum
  let monthlySalaryMinimumInSalaryCurrency = monthlySalaryMinimum
  const { currencyConversionData } = useCurrency()
  const { calculateAmountInNewCurrency } = useAmountAndCurrency()
  if (
    monthlySalaryMaximum !== undefined &&
    monthlySalaryMinimum !== undefined &&
    !!currentGovernmentJobTitle &&
    restProps.currency !== currentGovernmentJobTitle.currency
  ) {
    monthlySalaryMaximumInSalaryCurrency = calculateAmountInNewCurrency(
      currentGovernmentJobTitle.currency,
      restProps.currency as Currency,
      currencyConversionData,
      monthlySalaryMaximumInSalaryCurrency,
    )
    monthlySalaryMinimumInSalaryCurrency = calculateAmountInNewCurrency(
      currentGovernmentJobTitle.currency,
      restProps.currency as Currency,
      currencyConversionData,
      monthlySalaryMinimumInSalaryCurrency,
    )
    if (monthlySalaryMaximumInSalaryCurrency === undefined || monthlySalaryMinimumInSalaryCurrency === undefined) {
      return <></>
    }
  }
  return (
    <Field name={name} validate={validate}>
      {({ field: { value }, form: { setFieldValue } }: FieldProps) => (
        <>
          <PerInputNumber
            name={name}
            value={value}
            onChange={(v) => {
              const empty = ''
              if (!isContractChange) {
                setFieldValue(name, (v && !isNaN(v)) || v === 0 ? v : empty)
              } else {
                const fieldMin: number = restProps.min || 0
                const isSelectedSalaryInValidRange: boolean =
                  monthlySalaryMaximumInSalaryCurrency !== undefined &&
                  monthlySalaryMinimumInSalaryCurrency !== undefined &&
                  monthlySalaryMonthsInYear !== undefined &&
                  monthlySalaryMonthsInYear !== 0 &&
                  v
                    ? isMonthlySalaryInRange(
                        v / monthlySalaryMonthsInYear,
                        monthlySalaryMinimumInSalaryCurrency,
                        monthlySalaryMaximumInSalaryCurrency,
                      )
                    : true
                if (!!setSelectedSalaryWarning && !!t) {
                  if (isSelectedSalaryInValidRange) {
                    setSelectedSalaryWarning(undefined)
                  } else {
                    setSelectedSalaryWarning(
                      t('appraisalMeeting.stepContractChanges.modal.selectedMonthlySalaryOutOfRange', {
                        hiringOfficeCode: currentGovernmentJobTitle ? currentGovernmentJobTitle.hiringOffice.code : undefined,
                        department: currentGovernmentJobTitle ? currentGovernmentJobTitle.department : undefined,
                        title: currentGovernmentJobTitle ? currentGovernmentJobTitle.title : undefined,
                        separator: MULTI_VALUE_SEPARATOR,
                      }),
                    )
                  }
                }
                if (v === undefined || v === null || v.toString() === empty) {
                  setFieldValue(name, undefined)
                  if (!!setSelectedSalaryWarning) {
                    setSelectedSalaryWarning(undefined)
                  }
                  const isCurrentSalaryInValidRange: boolean =
                    monthlySalaryMinimumInSalaryCurrency !== undefined &&
                    monthlySalaryMaximumInSalaryCurrency !== undefined &&
                    monthlySalaryMonthsInYear !== undefined &&
                    monthlySalaryMonthsInYear !== 0 &&
                    currentContractSalary
                      ? isMonthlySalaryInRange(
                          currentContractSalary / monthlySalaryMonthsInYear,
                          monthlySalaryMinimumInSalaryCurrency,
                          monthlySalaryMaximumInSalaryCurrency,
                        )
                      : true
                  if (!!setCurrentSalaryWarning && !!t) {
                    if (isCurrentSalaryInValidRange) {
                      setCurrentSalaryWarning(undefined)
                    } else {
                      setCurrentSalaryWarning(
                        t('appraisalMeeting.stepContractChanges.modal.currentMonthlySalaryOutOfRange', {
                          hiringOfficeCode: currentGovernmentJobTitle ? currentGovernmentJobTitle.hiringOffice.code : undefined,
                          department: currentGovernmentJobTitle ? currentGovernmentJobTitle.department : undefined,
                          title: currentGovernmentJobTitle ? currentGovernmentJobTitle.title : undefined,
                          separator: MULTI_VALUE_SEPARATOR,
                        }),
                      )
                    }
                  }
                } else {
                  if (isNaN(v)) {
                    if (value) {
                      setFieldValue(name, value)
                    } else {
                      setFieldValue(name, empty)
                    }
                  } else if (v < fieldMin) {
                    if (value === undefined || value === null || value.toString() === '') {
                      if (minInitialOffset !== undefined && minInitialOffset !== null) {
                        if (restProps.step) {
                          setFieldValue(name, minInitialOffset - restProps.step)
                        } else {
                          setFieldValue(name, minInitialOffset)
                        }
                      } else {
                        setFieldValue(name, empty)
                      }
                    } else {
                      setFieldValue(name, empty)
                    }
                  } else if (v === fieldMin && restProps.step && restProps.step === value) {
                    setFieldValue(name, empty)
                  } else {
                    if (value === undefined || value === null || value.toString() === '') {
                      if (minInitialOffset !== undefined && minInitialOffset !== null) {
                        if (restProps.step && v === restProps.step) {
                          setFieldValue(name, minInitialOffset + restProps.step)
                        } else {
                          setFieldValue(name, v)
                        }
                      } else {
                        setFieldValue(name, v)
                      }
                    } else {
                      setFieldValue(name, v)
                    }
                  }
                }
              }
            }}
            {...restProps}
          />
          {subLabel && (
            <PerText mt="xxxs" fontSize="s" fontWeight="thin" color="secondary">
              {subLabel}
            </PerText>
          )}
          {selectedSalaryWarning && (
            <PerText mt="xxxs" fontSize="xs" fontWeight="thin" color="warning">
              {selectedSalaryWarning}
            </PerText>
          )}
        </>
      )}
    </Field>
  )
}
